(() => {
  const selector = '.catchii-collection-grid .card-gallery .product-media__image';
  const cache = new Map();
  const states = new WeakMap();
  const full = { x: 0, y: 0, width: 1, height: 1 };

  function detectBounds({ data, width, height }) {
    const foreground = (x, y) => {
      const i = (y * width + x) * 4;
      return data[i + 3] > 24 && Math.min(data[i], data[i + 1], data[i + 2]) < 239;
    };
    let edges = 0;
    let colored = 0;
    for (let x = 0; x < width; x++) {
      colored += Number(foreground(x, 0)) + Number(foreground(x, height - 1));
      edges += 2;
    }
    for (let y = 0; y < height; y++) {
      colored += Number(foreground(0, y)) + Number(foreground(width - 1, y));
      edges += 2;
    }
    if (colored / edges > 0.08) return full;
    const rows = new Uint16Array(height);
    const columns = new Uint16Array(width);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (foreground(x, y)) { rows[y]++; columns[x]++; }
      }
    }
    const left = columns.findIndex(n => n >= 2);
    const top = rows.findIndex(n => n >= 2);
    if (left < 0 || top < 0) return full;
    let right = width - 1;
    let bottom = height - 1;
    while (columns[right] < 2) right--;
    while (rows[bottom] < 2) bottom--;
    const padding = Math.ceil(Math.max(width, height) * 0.025);
    const x = Math.max(0, left - padding);
    const y = Math.max(0, top - padding);
    const w = Math.min(width, right + padding + 1) - x;
    const h = Math.min(height, bottom + padding + 1) - y;
    if (w / width < 0.3 || h / height < 0.3) return full;
    return { x: x / width, y: y / height, width: w / width, height: h / height };
  }

  function measure(source) {
    const canvas = document.createElement('canvas');
    const ratio = Math.min(1, 256 / Math.max(source.naturalWidth, source.naturalHeight));
    canvas.width = Math.max(1, Math.round(source.naturalWidth * ratio));
    canvas.height = Math.max(1, Math.round(source.naturalHeight * ratio));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return full;
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return detectBounds(context.getImageData(0, 0, canvas.width, canvas.height));
  }

  function readBounds(image, url) {
    if (cache.has(url)) return cache.get(url);
    const result = (async () => {
      try { return measure(image); } catch {
        try {
          const probe = new Image();
          probe.crossOrigin = 'anonymous';
          const preview = new URL(url, location.href);
          preview.searchParams.set('width', '256');
          probe.src = preview.href;
          await probe.decode();
          return measure(probe);
        } catch { return full; }
      }
    })();
    cache.set(url, result);
    return result;
  }

  function layout(image) {
    const state = states.get(image);
    if (!state?.bounds || !image.isConnected) return;
    const media = image.closest('.product-media');
    const width = media.clientWidth;
    const height = media.clientHeight;
    if (!width || !height || !image.naturalHeight) return;
    const box = state.bounds;
    const padding = Math.min(width, height) * 0.08;
    const aspect = image.naturalWidth / image.naturalHeight;
    const imageHeight = Math.min((width - padding * 2) / (box.width * aspect), (height - padding * 2) / box.height);
    const imageWidth = imageHeight * aspect;
    image.style.setProperty('--catchii-image-width', `${imageWidth}px`);
    image.style.setProperty('--catchii-image-height', `${imageHeight}px`);
    image.style.setProperty('--catchii-image-left', `${(width - imageWidth * box.width) / 2 - imageWidth * box.x}px`);
    image.style.setProperty('--catchii-image-top', `${(height - imageHeight * box.height) / 2 - imageHeight * box.y}px`);
    image.classList.add('catchii-image-normalized');
  }

  async function update(image) {
    if (!image.complete || !image.naturalWidth) return;
    const state = states.get(image);
    const url = image.currentSrc || image.src;
    if (state.url === url) { layout(image); return; }
    state.url = url;
    const bounds = await readBounds(image, url);
    if (state.url !== url || !image.isConnected) return;
    state.bounds = bounds;
    layout(image);
  }

  function scan(scope) {
    const images = scope.matches?.(selector) ? [scope] : [];
    images.push(...scope.querySelectorAll(selector));
    images.forEach(image => {
      if (states.has(image)) return;
      const media = image.closest('.product-media');
      const observer = new ResizeObserver(() => layout(image));
      states.set(image, { observer });
      observer.observe(media);
      image.addEventListener('load', () => update(image));
      update(image);
    });
  }

  if (!('ResizeObserver' in window)) return;
  scan(document);
  new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1) scan(node);
    }));
    records.forEach(record => record.removedNodes.forEach(node => {
      if (node.nodeType !== 1 || node.isConnected) return;
      const images = node.tagName === 'IMG' ? [node] : [];
      images.push(...node.querySelectorAll('img'));
      images.forEach(image => {
        const state = states.get(image);
        if (state && !image.isConnected) { state.observer.disconnect(); states.delete(image); }
      });
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
